/**
 * Editor — main orchestrator of @eyejack/editor. Subclasses web-core's AppContent
 * (engine + scene + camera + render-loop + content runner are shared with the
 * boilerplates) and implements the IEditor contract so the public API stays
 * plain-data and engine-agnostic.
 *
 * The AR-app features baked into AppBabylon/AppBase (MediaRecorder, Share, XR)
 * are disabled via the protected init/no-op overrides, matching how the non-AR
 * apps (e.g. AppSim3d) opt out. The active scene is the `content` (an
 * EditorContent) set via setContent; the editor layers on selection, gizmos,
 * picking, history and the dat.gui connectors.
 */

import {
  Engine,
  Scene,
  Color4,
  Vector3,
  HemisphericLight,
  type TransformNode,
} from 'babylonjs';

import AppContent from '@webcore/apps/app.content';
import { EventBus } from '@shared/events/EventBus';
import { EditorState, type ElementState } from '@shared/EditorState';
import { History, elementsEqual } from '@shared/History';
import { ControlModeTypes } from '@shared/constants';
import { WIDTH, HEIGHT, POV_POSITION, GRID_SIZE } from '@shared/constants';
import { unityToThreejs, threejsToUnity } from '@shared/SceneConfigConverter';
import type { EditorEvents } from '@shared/events/EventTypes';
import type { CoordinateSpace, SceneConfig, EditorOptions, AssetDefinition } from '@shared/types';
import type { IEditor } from '@shared/IEditor';

import { AssetManager } from '../assets/AssetManager';
import { ThumbnailGenerator } from '../assets/ThumbnailGenerator';
import { CameraController } from '../input/CameraController';
import { Picker } from '../input/Picker';
import { TransformController } from './TransformController';
import { EditorContent, type SceneConfigData } from '../content/EditorContent';
import { Skybox } from '../scene/Skybox';
import { HumanFigure } from '../scene/HumanFigure';
// Shared floor grid (web-core) — the same DCC-style grid the viewer/boilerplates use.
import CreatorGrid from '@webcore/core/creator/creator.grid';
import { getTransformElementPayload, clampToSceneBounds } from '../utils/BabylonEditorUtils';

export class Editor extends AppContent implements IEditor {
  readonly events: EventBus<EditorEvents>;

  private state: EditorState;

  // Built in initialize() (after the scene/camera exist) — definite-assignment.
  private assetManager!: AssetManager;
  private thumbnailGenerator!: ThumbnailGenerator;
  private cameraController!: CameraController;
  private picker!: Picker;
  private transformController!: TransformController;
  private skybox!: Skybox;
  private humanFigure!: HumanFigure;
  private gridPlane!: CreatorGrid;

  private activeTransformedMesh: TransformNode | null = null;

  // Undo/redo command stack (snapshots of state.elements). Owned by the editor;
  // hosts mirror restored state via the 'elements:replaced' event.
  private history = new History();
  private applyingHistory = false;

  // Clipboard for copy/paste of a single element (editor-owned, in-memory).
  private clipboard: ElementState | null = null;

  constructor(container: HTMLElement, options?: EditorOptions) {
    const width = options?.width ?? WIDTH;
    const height = options?.height ?? HEIGHT;

    // Create + size the canvas BEFORE super() (no `this` access allowed yet). The
    // host wrapper div has no intrinsic height, so explicit pixel CSS avoids a
    // collapsed canvas (which would desync picking); the backing buffer is
    // engine.resize()'d to the device pixel ratio in initEngine().
    const canvas = document.createElement('canvas');
    canvas.style.display = 'block';
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    canvas.style.outline = 'none';

    super(canvas); // AppContent → AppBabylon → AppBase

    container.appendChild(canvas);

    this.events = new EventBus<EditorEvents>();
    this.state = new EditorState(this.events);

    // Runs initialize() (our init* overrides) + starts the render loop.
    this.init();
  }

  // ============================================================ App lifecycle

  protected override initEngine(): void {
    // stencil (for the gizmos) + adaptToDeviceRatio=true (retina).
    this.engine = new Engine(this.renderCanvas!, true, { stencil: true }, true);
    this.engine.resize(true); // backing buffer = CSS size × devicePixelRatio
  }

  protected override initScene(): void {
    this.scene = new Scene(this.engine!);
    this.scene.useRightHandedSystem = true; // right-handed, Y-up (the internal space)
    this.scene.clearColor = new Color4(1, 1, 1, 1);

    // Lighting (panels are unlit/emissive; light is harmless and aids lit meshes).
    const hemi = new HemisphericLight('hemi', new Vector3(0, 1, 0), this.scene);
    hemi.intensity = 1;

    // IBL so PBR GLB models aren't flat/dark (panels are unlit, unaffected). NOTE:
    // the default helper pulls a prefiltered .env from the Babylon CDN — fine for
    // the online dev standalone; harden later with a bundled local .env.
    this.scene.createDefaultEnvironment({ createSkybox: false, createGround: false });
  }

  protected override initCamera(): void {
    this.cameraController = new CameraController(this.scene!, this.renderCanvas!);
    this.camera = this.cameraController.camera;
  }

  // Disable the AR-app features baked into AppBabylon/AppBase (the editor is a
  // non-AR, host-embedded tool).
  protected override initMediaRecorder(): void { /* disabled */ }
  protected override initShare(): void { /* disabled */ }

  protected override initCallbacks(): void {
    // Host-driven resize (see resize() below); start only the render loop — no
    // window resize listener.
    this.engine!.runRenderLoop(this.renderCallback.bind(this));
  }

  protected override initialize(): void {
    super.initialize(); // initEngine, initScene, initCamera, initRootNode, initMediaRecorder(noop), initShare(noop), initCallbacks

    // Scene + camera exist now → build the editor systems.
    this.assetManager = new AssetManager(this.scene!, this.state, this.events);
    this.thumbnailGenerator = new ThumbnailGenerator(this.assetManager, this.events, 128, 128, 1);

    this.transformController = new TransformController(this.scene!, {
      onDragStart: (node) => this._onGizmoDragStart(node),
      onDrag: (node) => this._onGizmoDrag(node),
      onDragEnd: (node) => this._onGizmoDragEnd(node),
    });

    this.picker = new Picker(this.scene!, {
      onSelect: (sceneID) => this.selectElement(sceneID),
      onDeselect: () => this.deselectAll(),
      onFocus: (sceneID) => this.focusElement(sceneID),
      // No hover highlighting while a gizmo transform is in progress.
      isHoverSuppressed: () => this.state.navigationLocked,
    });

    this.skybox = new Skybox(this.scene!, this.events);
    this.humanFigure = new HumanFigure(
      this.scene!,
      { x: POV_POSITION.x, y: POV_POSITION.y, z: POV_POSITION.z },
      this.events,
      this.cameraController.camera,
    );
    this.gridPlane = new CreatorGrid(this.scene!, GRID_SIZE);

    this._bindInternalEvents();
  }

  // Per-frame: AppBabylon's renderCallback computes timeDelta then calls render()
  // + scene.render(). AppContent.render drives the active content; we add the
  // thumbnail generator's throttled update.
  protected override render(): void {
    super.render(); // → this.content?.render(this.timeDelta)
    this.thumbnailGenerator.update(this.timeDelta);
  }

  // Disable AR lifecycle (only ever reachable via the global app emitter, which
  // the editor never drives — overridden for safety + clarity).
  public override async loadAsync(): Promise<void> { /* no content preload */ }
  public override async startXR(): Promise<void> { /* not an XR app */ }
  public override async stopXR(): Promise<void> { /* not an XR app */ }

  /** The active scene's content (AppContent.content), typed for editor use. */
  private get activeContent(): EditorContent | undefined {
    return this.content as EditorContent | undefined;
  }

  // ============================================================ Public API

  registerAssets(assets: AssetDefinition[]): void {
    this.assetManager.registerAssets(assets);
  }

  loadSceneConfig(config: SceneConfig, coordinateSpace: CoordinateSpace = 'threejs'): void {
    // A fresh load is a new baseline — you can't undo past it.
    this.history.clear();
    this._emitHistoryChanged();

    let sceneNodes: SceneConfigData[];
    let allElements: ElementState[];

    if (coordinateSpace === 'unity') {
      const internal = unityToThreejs({
        version: typeof config.version === 'number' ? config.version : 1,
        settings: (config.settings as { movable: boolean }) ?? { movable: true },
        assets: [],
        scenes: config.scenes,
      });
      sceneNodes = internal.scenes;
      allElements = internal.elements;
    } else {
      sceneNodes = config.scenes.map((s) => ({
        sceneID: s.sceneID,
        position: s.position,
        rotation: s.rotation,
        scale: s.scale,
      }));
      allElements = config.scenes.flatMap((s) =>
        s.children.map((child) => ({
          sceneID: child.sceneID,
          assetID: child.assetID,
          parentID: s.sceneID,
          position: child.position,
          rotation: child.rotation,
          scale: child.scale,
        })),
      );
    }

    this.state.setScenes(sceneNodes.map((s) => ({
      sceneID: s.sceneID, position: s.position, rotation: s.rotation, scale: s.scale,
    })));
    this.state.setElements(allElements);

    // The editor renders a single active scene (the first). Its content is driven
    // by the app's content runner (setContent → init/render/dispose).
    const activeNode = sceneNodes[0];
    if (activeNode) {
      const content = new EditorContent(
        activeNode, this.state, this.events, this.assetManager, (sceneID) => this.selectElement(sceneID),
      );
      // EditorContent.init() does only synchronous setup, so setContent's await
      // runs it to completion before yielding — the content is ready for the
      // handleElementChanges call below (we intentionally don't await the promise).
      void this.setContent(content);
      this.state.setBootstrapComplete(true);
      content.handleElementChanges(allElements.filter((el) => el.parentID === activeNode.sceneID));
    } else {
      this.clearContent();
      this.state.setBootstrapComplete(true);
    }

    this.events.emit('scene:loaded');
  }

  getSceneConfig(coordinateSpace: CoordinateSpace = 'threejs'): SceneConfig {
    if (coordinateSpace === 'unity') {
      const result = threejsToUnity({
        version: 1, settings: { movable: true }, assets: [],
        scenes: this.state.scenes, elements: this.state.elements,
      });
      return { scenes: result.scenes, settings: result.settings, version: String(result.version) };
    }
    const scenes = this.state.scenes.map((s) => ({
      sceneID: s.sceneID, position: s.position, rotation: s.rotation, scale: s.scale,
      children: this.state.elements.filter((el) => el.parentID === s.sceneID).map((el) => ({
        sceneID: el.sceneID, assetID: el.assetID, position: el.position, rotation: el.rotation, scale: el.scale,
      })),
    }));
    return { scenes };
  }

  getState(): Readonly<EditorState> {
    return this.state;
  }

  addElement(element: ElementState): void {
    this._recordHistory();
    const elements = [...this.state.elements, element];
    this.state.setElements(elements);
    this.events.emit('element:added', { elementId: element.sceneID });
    const content = this.activeContent;
    if (content && element.parentID === content.sceneID) {
      content.handleElementChanges(elements.filter((el) => el.parentID === content.sceneID));
    }
  }

  removeElement(elementId: string): void {
    if (this.state.selectedElements.includes(elementId)) this.deselectAll();
    this._recordHistory();
    const removed = this.state.elements.find((el) => el.sceneID === elementId);
    const elements = this.state.elements.filter((el) => el.sceneID !== elementId);
    this.state.setElements(elements);
    this.events.emit('element:removed', { elementId });
    const content = this.activeContent;
    if (content && removed && removed.parentID === content.sceneID) {
      content.handleElementChanges(elements.filter((el) => el.parentID === content.sceneID));
    }
  }

  selectElement(elementId: string): void {
    this.state.setSelectedElements([elementId]);
  }

  deselectAll(): void {
    this.state.setSelectedElements([]);
  }

  handleElementChanges(newElements: ElementState[]): void {
    // Host-driven changes (web Vuex round-trip): record the pre-change state for
    // undo, unless this is a no-op echo (e.g. a gizmo transform already committed
    // editor-side, or the initial-load re-sync).
    if (!elementsEqual(newElements, this.state.elements)) {
      this._recordHistory();
    }
    this.state.setElements(newElements);
    const content = this.activeContent;
    if (content) {
      content.handleElementChanges(newElements.filter((el) => el.parentID === content.sceneID));
    }
  }

  transformElementDirect(sceneID: string, position: number[], rotation: number[], scale: number[]): void {
    const panel = this.activeContent?.getElementById(sceneID);
    panel?.setTransform({ position, rotation, scale });
  }

  // ============================================================ Undo / redo

  undo(): void {
    const snapshot = this.history.undo(this.state.elements);
    if (snapshot) this._applyHistorySnapshot(snapshot.elements);
  }

  redo(): void {
    const snapshot = this.history.redo(this.state.elements);
    if (snapshot) this._applyHistorySnapshot(snapshot.elements);
  }

  canUndo(): boolean {
    return this.history.canUndo;
  }

  canRedo(): boolean {
    return this.history.canRedo;
  }

  // ============================================================ Copy / paste

  /** Copy the currently selected element's data to the clipboard. */
  copySelectedElement(): boolean {
    const id = this.state.selectedElements[0];
    if (!id) return false;
    const el = this.state.elements.find((e) => e.sceneID === id);
    if (!el) return false;
    this.clipboard = this._cloneElement(el);
    return true;
  }

  /**
   * Paste a copy of the clipboard element: new sceneID, same spot, added to the
   * scene (undoable), auto-selected with the translate gizmo so it can be dragged
   * immediately.
   */
  pasteElement(): boolean {
    if (!this.clipboard) return false;
    const src = this.clipboard;
    const pasted: ElementState = {
      sceneID: this._newSceneID(),
      assetID: src.assetID,
      parentID: src.parentID,
      position: [...src.position],
      rotation: [...src.rotation],
      scale: [...src.scale],
    };

    this.addElement(pasted); // records history + creates mesh + auto-selects the single add
    this.selectElement(pasted.sceneID); // ensure selected (idempotent with auto-select)
    this.setTransformMode('translate'); // ready to move straight away

    // Mirror the new element list to hosts (web Vuex) so the paste persists.
    this.events.emit('elements:replaced', { elements: this.state.elements });
    return true;
  }

  private _cloneElement(el: ElementState): ElementState {
    return {
      sceneID: el.sceneID,
      assetID: el.assetID,
      parentID: el.parentID,
      position: [...el.position],
      rotation: [...el.rotation],
      scale: [...el.scale],
    };
  }

  private _newSceneID(): string {
    const rand = globalThis.crypto?.randomUUID?.()
      ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    return `paste-${rand}`;
  }

  setTransformMode(mode: 'translate' | 'rotate' | 'scale'): void {
    this.state.setTransformControlsMode(mode);
  }

  setTransformSpace(space: 'world' | 'local'): void {
    this.state.setTransformControlsSpace(space);
  }

  setSnapping(enabled: boolean): void {
    this.state.setTransformControlsSnapping(enabled);
  }

  resetCamera(): void {
    this.cameraController.reset();
  }

  focusElement(elementId: string): void {
    const panel = this.activeContent?.getElementById(elementId);
    if (panel) this.cameraController.focusElement(panel.anchor);
  }

  public override resize(width?: number, height?: number): void {
    // Host-driven: update the CSS (display) size, then engine.resize() recomputes
    // the backing buffer from clientWidth/Height × devicePixelRatio (keeps picking
    // coordinates aligned). Also forwards to the active content.
    if (width !== undefined && height !== undefined) {
      this.renderCanvas!.style.width = `${width}px`;
      this.renderCanvas!.style.height = `${height}px`;
    }
    this.engine!.resize(true);
    this.content?.resize();
  }

  mute(): void {
    this.state.setMuted(true);
    this.assetManager.mute();
  }

  unmute(): void {
    this.state.setMuted(false);
    this.assetManager.unmute();
  }

  public override dispose(): void {
    this.engine?.stopRenderLoop();
    super.dispose(); // AppContent.dispose → clearContent (EditorContent.dispose) + AppBase emitter off
    this.assetManager.cleanup();
    this.thumbnailGenerator.cleanup();
    this.transformController.dispose();
    this.picker.dispose();
    this.cameraController.dispose();
    this.skybox.cleanup();
    this.humanFigure.cleanup();
    this.gridPlane.dispose();
    this.scene?.dispose();
    this.engine?.dispose();
    if (this.renderCanvas?.parentNode) this.renderCanvas.parentNode.removeChild(this.renderCanvas);
    this.state.reset();
    this.events.dispose();
  }

  // ============================================================ Internal

  private _bindInternalEvents(): void {
    this.events.on('transformMode:changed', ({ mode }) => this.transformController.setMode(mode));
    this.events.on('transformSpace:changed', ({ space }) => this.transformController.setSpace(space));
    this.events.on('snapping:changed', ({ enabled }) => this.transformController.setSnapping(enabled));

    this.events.on('element:selected', ({ elementId }) => {
      const panel = this.activeContent?.getElementById(elementId);
      if (panel) this.transformController.attach(panel.anchor);
    });
    this.events.on('element:deselected', () => this.transformController.detach());
  }

  private _onGizmoDragStart(node: TransformNode): void {
    this.cameraController.disable();
    this.state.setNavigationLocked(true);
    this.state.setControlMode(ControlModeTypes.TRANSFORM);
    this.activeTransformedMesh = node;
  }

  private _onGizmoDrag(node: TransformNode): void {
    // Keep the element within the grid bounds while dragging the gizmo.
    clampToSceneBounds(node.position);
    const payload = getTransformElementPayload(node);
    this.events.emit('element:transforming', {
      elementId: (node.metadata?.sceneID as string) ?? '',
      position: payload.position,
      rotation: payload.rotation,
      scale: payload.scale,
    });
  }

  private _onGizmoDragEnd(node: TransformNode): void {
    this.cameraController.enable();
    this.state.setNavigationLocked(false);
    clampToSceneBounds(node.position);
    const payload = getTransformElementPayload(node);
    const elementId = (node.metadata?.sceneID as string) ?? '';
    // Commit the new transform into authoritative state + record history here, so
    // the editor is self-consistent without relying on a host round-trip. The host
    // still gets element:transformed (below); when it round-trips the same values
    // back through handleElementChanges, that's deduped as a no-op.
    this._commitTransform(elementId, payload.position, payload.rotation, payload.scale);
    this.events.emit('element:transformed', {
      elementId,
      position: payload.position,
      rotation: payload.rotation,
      scale: payload.scale,
    });
    this.activeTransformedMesh = null;
  }

  /** Write a single element's transform into state.elements, recording history. */
  private _commitTransform(
    elementId: string,
    position: number[],
    rotation: number[],
    scale: number[],
  ): void {
    if (!this.state.elements.some((el) => el.sceneID === elementId)) return;
    const updated = this.state.elements.map((el) =>
      el.sceneID === elementId ? { ...el, position, rotation, scale } : el,
    );
    if (elementsEqual(updated, this.state.elements)) return; // click without drag → no change
    this._recordHistory();
    this.state.setElements(updated);
  }

  /** Push the current element state onto the undo stack (skips load + replay). */
  private _recordHistory(): void {
    if (!this.state.bootstrapComplete || this.applyingHistory) return;
    this.history.record(this.state.elements);
    this._emitHistoryChanged();
  }

  /** Restore an element snapshot: update state, rebuild meshes, notify the host. */
  private _applyHistorySnapshot(elements: ElementState[]): void {
    this.applyingHistory = true;
    // The gizmo may be attached to an element that's about to move or disappear.
    this.deselectAll();
    this.state.setElements(elements);
    const content = this.activeContent;
    if (content) {
      content.handleElementChanges(elements.filter((el) => el.parentID === content.sceneID));
    }
    this.applyingHistory = false;
    // Let hosts (web Vuex) mirror the restored state so it persists. The host's
    // resulting handleElementChanges echo is deduped (state already equals this).
    this.events.emit('elements:replaced', { elements });
    this._emitHistoryChanged();
  }

  private _emitHistoryChanged(): void {
    this.events.emit('history:changed', {
      canUndo: this.history.canUndo,
      canRedo: this.history.canRedo,
    });
  }
}

export default Editor;
